import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import FormContainer from 'components/Forms';
import fieldSelection from 'components/Forms/Fields';
import { makeStyles } from '@material-ui/core';
import { getUserId } from 'reducers/users';
import { v4 as uuidv4 } from 'uuid';
import { getSystemTimeZone } from 'reducers/utils';
import PopUp from 'components/Operational/PopUp/PopUp';
import Portal from 'portal/Portal';
import { SHIFT_FORM_CONSTANTS } from 'utils/constants/attendanceTracking';
import { useTranslation } from 'react-i18next';
import { attendancePageActions, attendanceSelectors } from '../';
import {
  fetchLastActivityTime,
  fetchNextActivityTime,
  maxDateRestrictor,
  minDateRestrictor,
  translateActivityType,
  reorderAccordingRecentActivity,
  transformNewShiftActivityObject,
  transformUpdateShiftActivityObject,
  deleteMatchingActivity,
  activityTypeEnums,
} from './attendanceTrackingHelper';
import {
  validateLastActivity,
  validateRequired,
  validationObject,
} from 'utils/validations';

const useStyles = makeStyles(() => ({
  root: {
    flexWrap: 'wrap',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const availableNewActivityType = lastActivity => {
  switch (lastActivity.activityType) {
    case activityTypeEnums.startShift:
    case activityTypeEnums.endTask:
    case activityTypeEnums.enterSite:
    case activityTypeEnums.exitSite:
    case activityTypeEnums.endBreak:
      return [
        {
          id: activityTypeEnums.endShift,
          name: 'Clock Off',
        },
        {
          id: activityTypeEnums.startBreak,
          name: 'Start Break',
        },
      ];
    case activityTypeEnums.startBreak:
      return [
        {
          id: activityTypeEnums.endBreak,
          name: 'Finish Break',
        },
      ];
    default:
      return [];
  }
};
export const createFormFields = ({
  shiftDetails,
  lastActivity,
  editMode,
  reorderedActivities,
}) => {
  return [
    {
      field: 'activityType',
      type: 'select',
      size: 'full',
      label: 'Activity Type',
      options: editMode
        ? shiftDetails.activityType
        : availableNewActivityType(lastActivity),
      required: true,
      disabled: editMode,
    },

    {
      field: 'activityTime',
      type: 'datetime',
      size: 'full',
      label: 'Activity Time',
      minDate: editMode
        ? minDateRestrictor(
            shiftDetails.activityType,
            fetchLastActivityTime(shiftDetails, reorderedActivities),
          )
        : minDateRestrictor(
            shiftDetails.activityType,
            lastActivity.activityTime,
          ),
      maxDate: editMode
        ? maxDateRestrictor(
            shiftDetails.activityType,
            fetchNextActivityTime(shiftDetails, reorderedActivities),
          )
        : undefined,
      required: true,
    },
  ];
};

export const validationSchema = ({
  lastActivity,
  currentActivity,
  activityType,
}) => {
  return validationObject({
    activityType: validateRequired(),
    ...validateLastActivity({ lastActivity, currentActivity, activityType }),
  });
};
const AttendanceTrackingShiftForm = ({
  editMode,
  closeForm,
  editModeValues,
  deleteMode = false,
  deleteModeValues,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();
  const [activityTime, setActivityTime] = useState();
  const [activityType, setActivityType] = useState();
  //--------------------------- SELECTORS -----------------------//
  const attendanceDetails = useSelector(state =>
    attendanceSelectors.getAttendanceDetailsRaw(state),
  );
  const currentUserId = useSelector(state => getUserId(state));
  const localTimezone = useSelector(state => getSystemTimeZone(state));
  //--------------------------- ACTIONS ------------------------//
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dispatchCreateShift = useCallback(
    shift => dispatch(attendancePageActions.addShiftItem({ shift }))[dispatch],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dispatchUpdateShift = useCallback(
    ({ shiftActivity, oldShiftActivityId }) =>
      dispatch(
        attendancePageActions.updateShiftItem({
          shiftActivity,
          oldShiftActivityId,
        }),
      )[dispatch],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dispatchDeleteShift = useCallback(
    shiftActivityId =>
      dispatch(attendancePageActions.deleteShiftItem({ shiftActivityId }))[
        dispatch
      ],
  );

  //-------------------------------------------------------------------------
  const reorderedActivities = reorderAccordingRecentActivity(
    attendanceDetails.activities,
  );

  const shiftInitialState = {
    activityType: undefined,
    activityTime: undefined,
    activityId: undefined,
  };

  const shiftEditState = values => {
    return {
      activityType: {
        id: values?.shiftInfo?.activityType,
        name: translateActivityType(values?.shiftInfo?.activityType),
      },
      activityTime: values?.shiftInfo?.activityTime,
      activityId: values?.shiftInfo?.id,
    };
  };

  const onCreate = values => {
    values.id = uuidv4();
    values.reporterId = currentUserId;

    const newShiftObject = transformNewShiftActivityObject({
      currentShiftDetails: attendanceDetails,
      newShiftInfo: values,
      localTimezone,
    });
    dispatchCreateShift({
      shift: newShiftObject,
    });
    closeForm();
  };

  const onEdit = values => {
    values.reporterId = currentUserId;
    const newShiftActivityUUID = uuidv4();
    const editedShiftActivityObject = transformUpdateShiftActivityObject({
      newShiftActivityUUID,
      newShiftActivityObject: values,
      localTimezone,
    });
    dispatchUpdateShift({
      shiftActivity: editedShiftActivityObject,
      oldShiftActivityId: editModeValues.id,
    });
    closeForm();
  };

  const onDelete = () => {
    const matchingActivityToBeDeleted = deleteMatchingActivity(
      deleteModeValues,
      reorderedActivities,
    );
    dispatchDeleteShift({
      shiftActivityId: deleteModeValues.id,
    });
    if (matchingActivityToBeDeleted) {
      dispatchDeleteShift({
        shiftActivityId: matchingActivityToBeDeleted.id,
      });
    }
    closeForm();
  };

  const lastActivity = isEmpty(reorderedActivities[0])
    ? []
    : reorderedActivities[0];

  const values = editMode ? shiftEditState(editModeValues) : shiftInitialState;
  const fields = createFormFields({
    shiftDetails: values,
    lastActivity: lastActivity,
    editMode,
    reorderedActivities: reorderedActivities,
  });
  const onChange = vals => {
    if (vals?.activityTime) {
      setActivityTime(vals.activityTime);
    }
    if (vals?.activityType) {
      setActivityType(vals.activityType);
    }
  };
  const deleteForm = () => {
    return (
      <>
        <PopUp
          title={SHIFT_FORM_CONSTANTS.deleteShiftHeader}
          statusMessage={SHIFT_FORM_CONSTANTS.deleteShiftMsg}
          submitBtnText={SHIFT_FORM_CONSTANTS.submitBtnText}
          cancelBtnText={SHIFT_FORM_CONSTANTS.cancelBtnText}
          cancel={closeForm}
          submit={onDelete}
          close={closeForm}
        />
      </>
    );
  };

  const mainForm = () => {
    return (
      <Portal id={'portal'} type="modal">
        {
          <FormContainer
            size="s"
            enableReinitialize={false}
            title={
              editMode
                ? t(SHIFT_FORM_CONSTANTS.editShift)
                : t(SHIFT_FORM_CONSTANTS.addShift)
            }
            requireHeaderSaveBtn
            onClickClose={closeForm}
            onSubmit={editMode ? onEdit : onCreate}
            initialValues={values}
            validationSchema={() =>
              validationSchema({
                lastActivity: lastActivity?.activityTime,
                currentActivity: activityTime,
                activityType,
              })
            }
            onChange={onChange}
          >
            {props => {
              return (
                <div className={classes.root}>
                  {fields.map((field, index) => {
                    return fieldSelection({
                      item: field,
                      key: index,
                      props: props,
                    });
                  })}
                </div>
              );
            }}
          </FormContainer>
        }
      </Portal>
    );
  };

  return deleteMode ? deleteForm() : mainForm();
};

AttendanceTrackingShiftForm.propTypes = {
  editMode: PropTypes.bool,
  deleteMode: PropTypes.bool,
  closeForm: PropTypes.func,
  editModeValues: PropTypes.object,
  deleteModeValues: PropTypes.object,
};

export default AttendanceTrackingShiftForm;
